import React from 'react';
import styled from 'styled-components';
import {useTheme} from 'styled-components';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import TextInput from '../../textInput';
import Icon from '../../icon';
import {ChatFooterProps} from '../../../interfaces/components/chatFooter';
import MyPressable from '../../myPressable';
import {Theme} from '../../../theme/lightTheme';
import Animated, {
  SlideInDown,
  SlideOutDown,
  ZoomIn,
  ZoomOut,
} from 'react-native-reanimated';
import {View, Platform} from 'react-native';

const isWeb = Platform.OS === 'web';

const ContainerWrapper = styled(isWeb ? 'div' : View)<{
  paddingHorizontal: number;
  width: string;
}>`
  width: ${({width}) => width};
  display: flex;
  align-self: center;
  flex-direction: column;
  padding-left: ${({paddingHorizontal}) => paddingHorizontal}px;
  padding-right: ${({paddingHorizontal}) => paddingHorizontal}px;
`;

const Container = styled(isWeb ? 'div' : Animated.View)<{
  bottomInset: number;
  isRow?: boolean;
  paddingLeft: number;
  paddingRight: number;
  paddingTop: number;
  paddingBottom: number;
}>`
  width: 100%;
  padding-left: ${({paddingLeft}) => paddingLeft}px;
  padding-right: ${({paddingRight}) => paddingRight}px;
  background-color: ${({theme}) => theme.textInput.background};
  border-radius: ${({isRow}) => (isRow ? 300 : 20)}px;
  border-width: ${({isRow}) => (isRow ? 1 : 0)}px;
  border-color: ${({theme}) => theme.primary};
  overflow: hidden;
  ${({isRow, bottomInset, paddingTop, paddingBottom}) =>
    !isRow &&
    `padding-bottom: ${Math.max(bottomInset, paddingBottom)}px;padding-top: ${paddingTop}px;border-bottom-left-radius:0;border-bottom-right-radius:0;`};
  ${isWeb &&
  `
    display: flex;
    flex-direction: column;
    flex: 1;
    max-width: 100%;
    border-radius: 25px;
    margin: 8px 0;
    padding: 8px 12px;
    border-bottom-left-radius: 25px;
    border-bottom-right-radius: 25px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  `}
`;

const ContentContainer = styled(isWeb ? 'div' : View)<{isRow?: boolean}>`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  ${({isRow}) => isRow && 'justify-content: space-between'};
  ${isWeb &&
  `
    flex: 1;
    max-width: 100%;
  `}
`;

const InputBox = styled(TextInput)<{isRow?: boolean; width: string}>`
  background-color: transparent;
  padding-left: 5%;
  padding-right: 5%;
  border-radius: ${({isRow}: {isRow?: boolean}) => (isRow ? 300 : 20)}px;
  ${({isRow, width}) => isRow && `width: ${width}px`};
  ${isWeb &&
  `
    width: ${({isRow, width}: {isRow?: boolean; width: number}) => (isRow ? `${width}px` : '100%')};
    min-height: 40px;
    flex: 1;
    border-radius: 20px;
    padding: 8px 12px;
    margin: 4px 8px;
  `}
`;

const IconContainer = styled(MyPressable)`
  border-width: 1px;
  border-color: ${({theme}) => theme.primary + '22'};
  border-radius: 100px;
  ${({theme}) =>
    isWeb
      ? `
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 40px;
    margin: 4px;
    transition: all 0.2s ease;
    &:hover {
      transform: scale(1.05);
    }
    &:active {
      transform: scale(0.95);
    }
  `
      : `padding: ${theme.text.s10}px;`}
`;

const Badge = styled(isWeb ? 'div' : Animated.View)`
  width: ${({theme}) => theme.text.s11}px;
  aspect-ratio: 1;
  border-radius: 100px;
  background-color: ${({theme}) => theme.secondary};
  border-width: 1px;
  border-color: ${({theme}) => theme.text.light};
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
`;

const Row = styled(isWeb ? 'div' : View)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  ${isWeb &&
  `
    padding: 8px 0;
  `}
`;

/**
 * Renders the chat input area with an input box and a send icon.
 *
 * @component
 *
 * @param hideAttachButton
 * @param onAttachPress
 * @param isAttachBadge
 * @param isRecording
 * @param value
 * @param {ChatFooterProps} props - The props for the ChatFooter component.
 * @param {Function} props.onSubmit - Callback function triggered upon pressing the send icon. It is expected to handle the submission of the input box's content.
 *
 * @returns {React.ReactElement} The ChatFooter component, comprising the styled input box and send icon within a container that respects safe area insets.
 */
const ChatFooter = ({
  onSubmit,
  hideAttachButton,
  onAttachPress,
  isAttachBadge,
  isRecording,
  value,
  isLoading,
  hideAnimation,
  hideSubmit,
  isRow,
  style,
  ...props
}: ChatFooterProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {wdp, hdp, getByScreenSize, isLargeScreen, isMobile} =
    useResponsiveDimensions();

  const _renderInputBox = () => {
    return (
      <InputBox
        isRow={isRow}
        hideFocusedEffect
        width={
          Platform.OS === 'web' && isLargeScreen
            ? `calc(${wdp(100)}px - ${Math.min(wdp(30), 350)}px)`
            : `${isRow && !hideSubmit ? wdp(65) : wdp(92)}px`
        }
        style={{
          paddingBottom: isWeb ? 0 : undefined,
        }}
        //@ts-ignore
        inputContainerStyle={{
          borderColor: 'transparent',
          maxHeight: theme.text.s7 * 10,
          width:
            Platform.OS === 'web' && isLargeScreen
              ? isRow && !hideSubmit
                ? wdp(55)
                : wdp(60)
              : isRow && !hideSubmit
                ? wdp(55)
                : wdp(70),
        }}
        scrollEnabled
        multiline
        blurOnSubmit
        returnKeyType={'send'}
        value={value}
        {...props}
        onSubmitEditing={!value || isLoading ? undefined : () => onSubmit?.()}
      />
    );
  };

  const _renderRecordIcon = () => {
    if (hideAttachButton) {
      return null;
    }
    return (
      <View>
        {isAttachBadge && <Badge entering={ZoomIn} exiting={ZoomOut} />}
        <IconContainer
          onPress={onAttachPress}
          borderRadius={100}
          containerStyle={{
            backgroundColor: 'transparent',
            padding: theme.text.s10,
          }}>
          <Icon
            name={isRecording ? 'pause' : 'record'}
            color={theme.primary}
            size={theme.text.s55}
            style={[isRecording && {transform: [{scale: 0.7}]}]}
          />
        </IconContainer>
      </View>
    );
  };

  const _renderSendIcon = () => {
    if (hideSubmit) {
      return null;
    }
    return (
      <IconContainer
        onPress={onSubmit}
        enableScale
        disabled={!value || isLoading}
        borderRadius={100}
        containerStyle={{
          backgroundColor: theme.text.light,
          padding: theme.text.s10,
        }}>
        <Icon name={'send'} size={theme.text.s55} color={theme.primary} />
      </IconContainer>
    );
  };

  return (
    <ContainerWrapper
      paddingHorizontal={
        Platform.OS === 'web' && isLargeScreen ? wdp(getByScreenSize(0, 1)) : 0
      }
      width={
        Platform.OS === 'web' && isLargeScreen
          ? `calc(${wdp(100)}px - ${Math.min(wdp(30), 350)}px)`
          : '100%'
      }>
      <Container
        isRow={isRow}
        style={style}
        entering={hideAnimation ? undefined : SlideInDown.delay(100)}
        exiting={hideAnimation ? undefined : SlideOutDown}
        bottomInset={bottomInset}
        paddingLeft={wdp(4)}
        paddingRight={wdp(4)}
        paddingTop={hdp(1)}
        paddingBottom={hdp(1)}>
        <ContentContainer isRow={isRow && isMobile}>
          {isRow && _renderRecordIcon()}
          {_renderInputBox()}
          {isRow && _renderSendIcon()}
        </ContentContainer>
        {!isRow && (
          <Row>
            {_renderRecordIcon()}
            {_renderSendIcon()}
          </Row>
        )}
      </Container>
    </ContainerWrapper>
  );
};

export default ChatFooter;
